import { ADMIN_LINK_PREFIX } from '../../Admin/Utils/LinkPrefix';
import { wipeDbSyncer } from '../../Datasource/LocalDB/DbSyncer';
import { getDb } from '../../Datasource/LocalDB/LocalDB';
import { type TlbSignedUserState } from '../../reducers/tlbUserReducer';
import { loadTlbUserForUserId } from '../Authentication/Reauthenticate';
import { AppEnvironment, setPreferredEnvironment } from '../BackendSwitcher';
import {
    clearImpersonationRecord,
    type ImpersonatedTlbUserState,
    IMPERSONATION_TLB_USER_SIGNED_OUT,
    type ImpersonationRecord,
    persistImpersonation,
} from './impersonation';

export type StartImpersonationParams = {
    realUserId: string;
    targetUserId: string;
    targetLabel: string;
    targetGroups: string[];
    environment: AppEnvironment;
};

// The local database is shared by both identities, so it gets wiped on the way in and on the
// way out - otherwise the impersonated user's flights and duties would linger in the admin's
// offline cache (and vice versa). Both switches end with a full page reload.
export const startImpersonation = async (params: StartImpersonationParams): Promise<void> => {
    const { realUserId, targetUserId, targetLabel, targetGroups, environment } = params;
    await assertNothingPendingToSync();

    const tlbUser = await getImpersonatedTlbUser(targetUserId, targetGroups);
    if (environment === AppEnvironment.TLB && tlbUser === IMPERSONATION_TLB_USER_SIGNED_OUT) {
        throw new Error(
            `Uživatel ${targetLabel} nemá crewable kontakt, TLB aplikaci za něj otevřít nelze. Použij OPS pohled.`
        );
    }

    const record: ImpersonationRecord = {
        version: 1,
        startedAt: Date.now(),
        realUserId,
        targetUserId,
        targetLabel,
        cognito: {
            groups: [...targetGroups],
            id: targetUserId,
            isAdmin: targetGroups.includes('adm'),
            dataIsStale: false,
        },
        tlbUser,
    };
    persistImpersonation(record);
    setPreferredEnvironment(environment);
    await resetLocalData();

    globalThis.location.href = `${globalThis.location.origin}${
        environment === AppEnvironment.TLB ? '/' : ADMIN_LINK_PREFIX
    }`;
};

export const stopImpersonation = async (): Promise<void> => {
    clearImpersonationRecord();
    setPreferredEnvironment(undefined);
    await resetLocalData();

    globalThis.location.href = `${globalThis.location.origin}${ADMIN_LINK_PREFIX}/impersonate`;
};

async function assertNothingPendingToSync(): Promise<void> {
    const pending = await getDb().syncItems.count();
    if (pending > 0) {
        throw new Error(
            `V lokální databázi čeká ${pending} nesynchronizovaných změn. Nech je nejdřív odeslat, jinak by se přepnutím ztratily.`
        );
    }
}

async function resetLocalData(): Promise<void> {
    await getDb().wipe();
    wipeDbSyncer();
}

async function getImpersonatedTlbUser(targetUserId: string, targetGroups: string[]): Promise<ImpersonatedTlbUserState> {
    const user = await loadTlbUserForUserId(targetUserId);
    if (!user) {
        return IMPERSONATION_TLB_USER_SIGNED_OUT;
    }
    const signedState: TlbSignedUserState = {
        data: user,
        isAdmin: targetGroups.includes('adm'),
        isPilotOrAircraft: targetGroups.includes('usr'),
        isPilot: !!user.userId && !user.aircraftId,
        isAircraft: !!user.aircraftId,
        isTechnician: targetGroups.includes('tch'),
        dataIsStale: false,
    };

    return signedState;
}
